import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  IndianRupee, Upload, Download, Plus, Trash2, Save, RefreshCw,
  Grid3x3, Weight, MapPin, FileSpreadsheet, Info, Copy, Building2, Percent, Timer,
} from "lucide-react";

const TABS = [
  { key: "matrix", label: "Rate Matrix", icon: Grid3x3 },
  { key: "slabs", label: "Weight Slabs", icon: Weight },
  { key: "zones", label: "Zone Matrix", icon: MapPin },
  { key: "surcharges", label: "Surcharges", icon: Percent },
  { key: "transit", label: "Transit Time (TAT)", icon: Timer },
];

export default function RateMatrixPage() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === "super_admin";
  const [tab, setTab] = useState("matrix");
  const [couriers, setCouriers] = useState([]);
  const [meta, setMeta] = useState({ service_types: [], zone_hierarchy: [], service_weight_limits_g: {} });
  const [courier, setCourier] = useState("");
  const [service, setService] = useState("Surface");
  const [tenants, setTenants] = useState([]);
  // scope: "platform" or a tenant id. Non-super users are always locked to their own tenant.
  const [scopeTid, setScopeTid] = useState(isPlatformAdmin ? "platform" : (user?.tenant_id || ""));
  const [copyOpen, setCopyOpen] = useState(false);
  // Phase 4 · Feb 2026 · Unified Branch Scope. Lifted from TransitTime tab so every
  // editor (Rate cells, Slabs, Zones, Surcharges, TAT) reads/writes the same scope.
  const [scopeBranch, setScopeBranch] = useState("");
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (!scopeTid || scopeTid === "platform") { setBranches([]); setScopeBranch(""); return; }
    api.get("/branches", { params: { include_all: 1 } })
       .then(r => setBranches((r.data || []).filter(b => b.tenant_id === scopeTid || !b.tenant_id)))
       .catch(() => setBranches([]));
  }, [scopeTid]);

  useEffect(() => {
    api.get("/couriers").then(r => {
      const list = (r.data || []).map(c => (typeof c === "string" ? c : c?.name)).filter(Boolean);
      setCouriers(list);
      if (!courier && list.length > 0) setCourier(list[0]);
    });
    api.get("/rate-matrix/service-types").then(r => setMeta(r.data));
    if (isPlatformAdmin) {
      api.get("/platform/tenants").then(r => setTenants(r.data || [])).catch(() => setTenants([]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scopeLabel = useMemo(() => {
    if (scopeTid === "platform" || !scopeTid) return "Mailit Default (Platform)";
    const t = tenants.find(x => x.id === scopeTid);
    return t ? `${t.name} · ${t.code}` : (user?.tenant_id === scopeTid ? "Your Company" : scopeTid);
  }, [scopeTid, tenants, user]);

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="rate-matrix-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><IndianRupee size={12} /> Dynamic Rate Engine</div>
          <h1 className="text-4xl font-display mt-1">Courier Rate Matrix.</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Configure zone maps, weight slabs, and per-zone rates for every courier + service type.
            The booking module resolves zone → slab → price from this matrix.
            <b className="ml-1">Priority:</b> Company matrix → Mailit Default → No rate.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCopyOpen(true)}
                  data-testid="rm-copy-open" className="rounded-md">
            <Copy size={13} className="mr-1" /> Copy Rate Matrix
          </Button>
        </div>
      </header>

      {/* Scope banner */}
      <div className="border border-primary/40 bg-red-50/40 rounded-md p-3 flex items-center gap-3 flex-wrap"
           data-testid="rm-scope-banner">
        <Building2 size={14} className="text-primary" />
        <span className="overline">Scope:</span>
        <b className="text-sm" data-testid="rm-scope-label">{scopeLabel}</b>
        {isPlatformAdmin && (
          <select value={scopeTid}
                  onChange={(e) => setScopeTid(e.target.value)}
                  data-testid="rm-scope-select"
                  className="h-8 rounded-md border border-border bg-card px-2 text-xs ml-auto">
            <option value="platform">🌐 Mailit Default (Platform)</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>🏢 {t.name} · {t.code}</option>
            ))}
          </select>
        )}
        {/* Phase 4 · Branch Scope selector — visible whenever a company scope is chosen */}
        {scopeTid && scopeTid !== "platform" && (
          <>
            <span className="overline ml-2">Branch:</span>
            <select value={scopeBranch}
                    onChange={(e) => setScopeBranch(e.target.value)}
                    data-testid="rm-branch-scope-select"
                    className="h-8 rounded-md border border-border bg-card px-2 text-xs">
              <option value="">— Company default (all branches) —</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name || b.code}</option>)}
            </select>
          </>
        )}
      </div>

      {/* Selectors */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <label className="block">
          <span className="overline">Courier</span>
          <select value={courier} onChange={(e) => setCourier(e.target.value)}
                  data-testid="rm-courier"
                  className="mt-1 h-10 w-full rounded-md border border-border bg-card px-3 text-sm">
            {couriers.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="overline">Service Type</span>
          <select value={service} onChange={(e) => setService(e.target.value)}
                  data-testid="rm-service"
                  className="mt-1 h-10 w-full rounded-md border border-border bg-card px-3 text-sm">
            {meta.service_types.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <div className="flex items-end">
          <div className="border border-border rounded-md p-2 text-[11px] text-muted-foreground bg-muted/30 w-full">
            <Info size={11} className="inline mr-1" />
            {meta.service_weight_limits_g?.[service]
              ? `Max weight for ${service}: ${meta.service_weight_limits_g[service]}g`
              : `Configurable per courier + ${service}`}
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border" data-testid="rm-tabs">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  data-testid={`rm-tab-${t.key}`}
                  className={`px-4 py-2 text-sm border-b-2 transition ${tab === t.key
                    ? "border-primary text-primary font-semibold"
                    : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon size={13} className="inline mr-1.5" /> {t.label}
          </button>
        ))}
      </div>

      {courier && tab === "matrix" && <RateMatrixEditor courier={courier} service={service} scopeTid={scopeTid} scopeBranch={scopeBranch} zoneHierarchy={meta.zone_hierarchy} />}
      {courier && tab === "slabs" && <WeightSlabsEditor courier={courier} service={service} scopeTid={scopeTid} scopeBranch={scopeBranch} maxG={meta.service_weight_limits_g?.[service]} />}
      {courier && tab === "zones" && <ZoneMatrixEditor courier={courier} scopeTid={scopeTid} scopeBranch={scopeBranch} />}
      {courier && tab === "surcharges" && <SurchargesEditor courier={courier} service={service} scopeTid={scopeTid} scopeBranch={scopeBranch} />}
      {courier && tab === "transit" && <TransitTimeEditor courier={courier} service={service} scopeTid={scopeTid} scopeBranch={scopeBranch} />}

      <CopyDialog open={copyOpen} onClose={() => setCopyOpen(false)}
                  tenants={tenants} isPlatformAdmin={isPlatformAdmin}
                  userTenantId={user?.tenant_id} couriers={couriers} serviceTypes={meta.service_types || []} />
    </div>
  );
}

/* --------------- RATE MATRIX EDITOR --------------- */
function RateMatrixEditor({ courier, service, scopeTid, scopeBranch, zoneHierarchy }) {
  const [data, setData] = useState({ zones: [], slabs: [], cells: [], used_fallback: false });
  const [dirty, setDirty] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fillValue, setFillValue] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importBusy, setImportBusy] = useState(false);

  const params = useMemo(() => {
    const p = { courier, service_type: service, tenant_id: scopeTid || "platform" };
    if (scopeBranch) p.branch_id = scopeBranch;
    return p;
  }, [courier, service, scopeTid, scopeBranch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rate-matrix/rates", { params });
      setData(data);
      setDirty({});
    } catch (e) { toast.error("Could not load rate matrix"); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const downloadTemplate = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/rate-matrix/rates/template`;
    const token = localStorage.getItem("token") || "";
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "rate_matrix_template.xlsx";
        a.click();
      }).catch(() => toast.error("Template download failed"));
  };

  const runImport = async (commit = 0) => {
    if (!importFile) return toast.error("Choose an Excel file first");
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post(`/rate-matrix/rates/import?commit=${commit}&tenant_id=${scopeTid || "platform"}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportPreview(data);
      if (commit) {
        toast.success(`Imported ${data.saved} rate row(s)`);
        setImportOpen(false); setImportFile(null); setImportPreview(null);
        load();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImportBusy(false); }
  };

  const cellMap = useMemo(() => {
    const m = new Map();
    data.cells.forEach(c => m.set(`${c.zone}|${c.slab_label}`, c.price));
    return m;
  }, [data.cells]);

  const getCell = (z, s) => {
    const k = `${z}|${s}`;
    if (dirty[k] !== undefined) return dirty[k];
    const v = cellMap.get(k);
    return v === undefined ? "" : String(v);
  };
  const setCell = (z, s, v) => setDirty(d => ({ ...d, [`${z}|${s}`]: v }));

  const fillDown = (slab) => {
    if (fillValue === "") return toast.error("Enter a value in the Fill field first");
    setDirty(d => {
      const next = { ...d };
      data.zones.forEach(z => { next[`${z}|${slab.label}`] = fillValue; });
      return next;
    });
  };
  const fillRight = (zone) => {
    if (fillValue === "") return toast.error("Enter a value in the Fill field first");
    setDirty(d => {
      const next = { ...d };
      data.slabs.forEach(s => { next[`${zone}|${s.label}`] = fillValue; });
      return next;
    });
  };
  const fillAll = () => {
    if (fillValue === "") return toast.error("Enter a value in the Fill field first");
    setDirty(d => {
      const next = { ...d };
      data.zones.forEach(z => data.slabs.forEach(s => { next[`${z}|${s.label}`] = fillValue; }));
      return next;
    });
  };

  const save = async () => {
    const cells = [];
    Object.entries(dirty).forEach(([k, v]) => {
      const [zone, slab_label] = k.split("|");
      const price = parseFloat(v);
      if (!isNaN(price) && price >= 0) cells.push({ zone, slab_label, price, active: true });
    });
    if (cells.length === 0) return toast.error("No changes to save");
    setBusy(true);
    try {
      const { data: r } = await api.put("/rate-matrix/rates", { cells }, { params });
      toast.success(`Saved ${r.saved} rate${r.saved === 1 ? "" : "s"} for this scope`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const undo = () => { setDirty({}); toast.info("Changes reverted"); };
  const dirtyCount = Object.keys(dirty).length;

  return (
    <section className="space-y-3" data-testid="rate-matrix-editor">
      {data.used_fallback && (
        <div className="text-[12px] p-2 border border-amber-300 bg-amber-50 rounded-md text-amber-900"
             data-testid="rm-fallback-banner">
          <b>Fallback active.</b> This company has no custom rates for {courier} · {service} yet — the Mailit Default matrix is being displayed. Any value you save here creates a company override.
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="overline">Fill:</span>
        <Input value={fillValue} onChange={(e) => setFillValue(e.target.value)}
                type="number" placeholder="₹" data-testid="rm-fill-value"
                className="w-24 h-9 rounded-md mono" />
        <Button variant="outline" size="sm" onClick={fillAll} data-testid="rm-fill-all"
                className="h-9 rounded-md">Fill all</Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={downloadTemplate}
                data-testid="rm-tpl-download" className="h-9 rounded-md">
          <Download size={13} className="mr-1" /> Template
        </Button>
        <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}
                data-testid="rm-import-open" className="h-9 rounded-md">
          <Upload size={13} className="mr-1" /> Import Excel
        </Button>
        <Button variant="outline" size="sm" onClick={undo} disabled={dirtyCount === 0}
                data-testid="rm-undo" className="h-9 rounded-md">
          <RefreshCw size={13} className="mr-1" /> Undo{dirtyCount ? ` (${dirtyCount})` : ""}
        </Button>
        <Button onClick={save} disabled={busy || dirtyCount === 0}
                data-testid="rm-save"
                className="h-9 rounded-md bg-primary text-primary-foreground shadow-brut-orange">
          <Save size={13} className="mr-1" /> Save all
        </Button>
      </div>

      {/* Rate Matrix Excel Import — Preview + Validate + Commit */}
      <Dialog open={importOpen} onOpenChange={(v) => { if (!v) { setImportOpen(false); setImportPreview(null); setImportFile(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="rm-import-dialog">
          <DialogHeader>
            <DialogTitle>Import Rate Matrix from Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-[11px] text-muted-foreground">
              Upload the same template downloaded above. First click "Preview" to validate. Errors are highlighted per row. Then "Confirm Import" saves the valid rows.
            </p>
            <input type="file" accept=".xlsx,.xls" data-testid="rm-import-file"
                    onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportPreview(null); }} />
            {importPreview && (
              <div className="border border-border rounded-md overflow-hidden max-h-80 overflow-y-auto" data-testid="rm-import-preview">
                <div className="p-2 bg-muted text-[11px] font-bold">
                  Total {importPreview.total_rows} · <span className="text-emerald-700">Valid {importPreview.valid_count}</span> · <span className="text-red-700">Skipped {importPreview.skipped_count}</span>
                </div>
                <table className="w-full text-[10px] mono">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr><th className="p-1 text-left">Row</th><th className="p-1 text-left">Courier</th><th className="p-1 text-left">Svc</th><th className="p-1 text-left">Zone</th><th className="p-1 text-left">Slab</th><th className="p-1 text-right">Freight</th><th className="p-1 text-left">Errors</th></tr>
                  </thead>
                  <tbody>
                    {importPreview.preview.map((p, i) => (
                      <tr key={i} className={`border-t border-border ${p.valid ? "bg-emerald-50/40" : "bg-red-50"}`}
                          data-testid={`rm-import-row-${p.row}`}>
                        <td className="p-1">{p.row}</td>
                        <td className="p-1">{p.raw?.courier}</td>
                        <td className="p-1">{p.raw?.service_type}</td>
                        <td className="p-1">{p.raw?.zone}</td>
                        <td className="p-1">{p.raw?.slab_label}</td>
                        <td className="p-1 text-right">{p.raw?.price}</td>
                        <td className="p-1 text-red-700">{(p.errors || []).join("; ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportPreview(null); setImportFile(null); }}
                    data-testid="rm-import-cancel">Cancel</Button>
            <Button variant="outline" onClick={() => runImport(0)} disabled={importBusy || !importFile}
                    data-testid="rm-import-preview-btn">
              {importBusy && !importPreview ? "Validating…" : "Preview"}
            </Button>
            <Button onClick={() => runImport(1)}
                    disabled={importBusy || !importPreview || importPreview.valid_count === 0}
                    data-testid="rm-import-commit"
                    className="bg-primary text-primary-foreground shadow-brut-orange">
              {importBusy ? "Importing…" : `Confirm Import (${importPreview?.valid_count || 0})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="p-10 text-center text-muted-foreground">Loading…</div>
      ) : data.slabs.length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-10 text-center">
          <div className="text-sm font-semibold mb-1">No weight slabs configured</div>
          <p className="text-[12px] text-muted-foreground">Open the Weight Slabs tab and add slabs for {courier} · {service} first.</p>
        </div>
      ) : (
        <div className="border border-border rounded-md overflow-x-auto bg-card">
          <table className="w-full text-xs mono" data-testid="rm-matrix-table">
            <thead className="bg-muted sticky top-0">
              <tr>
                <th className="text-left p-2 border-r border-border w-32">Zone \\ Slab</th>
                {data.slabs.map(s => (
                  <th key={s.label} className="text-left p-2 border-r border-border">
                    <div>{s.label}</div>
                    <button onClick={() => fillDown(s)} data-testid={`rm-fill-down-${s.label}`}
                            className="text-[9px] text-primary underline hover:text-primary/80"
                            title="Fill this column">↓ fill col</button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.zones.map(z => (
                <tr key={z} className="border-t border-border">
                  <th className="p-2 border-r border-border text-left bg-muted/40">
                    <div>{z}</div>
                    <button onClick={() => fillRight(z)} data-testid={`rm-fill-right-${z}`}
                            className="text-[9px] text-primary underline hover:text-primary/80"
                            title="Fill this row">→ fill row</button>
                  </th>
                  {data.slabs.map(s => {
                    const key = `${z}|${s.label}`;
                    const isDirty = dirty[key] !== undefined;
                    return (
                      <td key={key} className="p-1 border-r border-border">
                        <input value={getCell(z, s.label)}
                                onChange={(e) => setCell(z, s.label, e.target.value)}
                                data-testid={`rm-cell-${z}-${s.label}`}
                                type="number" step="0.01"
                                className={`w-full h-8 px-2 rounded border text-right ${isDirty ? "border-primary bg-red-50" : "border-transparent hover:border-border"}`} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* --------------- WEIGHT SLABS EDITOR --------------- */
function WeightSlabsEditor({ courier, service, scopeTid, scopeBranch, maxG }) {
  const [slabs, setSlabs] = useState([]);
  const [usedFallback, setUsedFallback] = useState(false);
  const [busy, setBusy] = useState(false);
  const params = useMemo(() => {
    const p = { courier, service_type: service, tenant_id: scopeTid || "platform" };
    if (scopeBranch) p.branch_id = scopeBranch;
    return p;
  }, [courier, service, scopeTid, scopeBranch]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/rate-matrix/weight-slabs", { params });
      setSlabs(data.slabs || []);
      setUsedFallback(!!data.used_fallback);
    } catch (e) { toast.error("Load failed"); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const addSlab = () => {
    const last = slabs[slabs.length - 1];
    const min_g = last ? (Number(last.max_g) + 1) : 0;
    setSlabs(s => [...s, { label: `${min_g}-${min_g + 500}g`, min_g, max_g: min_g + 500, order: s.length }]);
  };
  const update = (idx, field, val) => setSlabs(s => s.map((r, i) => i === idx ? { ...r, [field]: field === "label" ? val : Number(val) } : r));
  const remove = (idx) => setSlabs(s => s.filter((_, i) => i !== idx));

  const save = async () => {
    if (slabs.length === 0) return toast.error("Add at least one slab");
    for (const s of slabs) {
      if (!s.label?.trim()) return toast.error("Every slab must have a label");
      if (s.min_g < 0 || s.max_g <= s.min_g) return toast.error(`Invalid slab range for '${s.label}'`);
    }
    setBusy(true);
    try {
      await api.put("/rate-matrix/weight-slabs", { slabs }, { params });
      toast.success("Weight slabs saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <section className="space-y-3" data-testid="weight-slabs-editor">
      {usedFallback && (
        <div className="text-[12px] p-2 border border-amber-300 bg-amber-50 rounded-md text-amber-900"
             data-testid="slabs-fallback-banner">
          Showing Mailit Default slabs — this company has no custom slabs for {courier} · {service}. Saving here creates a company override.
        </div>
      )}
      {maxG && (
        <div className="text-[12px] p-2 border border-amber-300 bg-amber-50 rounded-md text-amber-800">
          {service}: system-enforced max weight = {maxG}g.
        </div>
      )}
      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted text-[10px] uppercase tracking-widest">
            <tr><th className="p-2 text-left">Label</th>
                <th className="p-2 text-left">Min (g)</th>
                <th className="p-2 text-left">Max (g)</th>
                <th className="p-2 w-16"></th></tr>
          </thead>
          <tbody>
            {slabs.map((s, i) => (
              <tr key={i} className="border-t border-border" data-testid={`slab-row-${i}`}>
                <td className="p-1"><Input value={s.label} onChange={(e) => update(i, "label", e.target.value)}
                                            data-testid={`slab-label-${i}`} className="h-8 rounded-md" /></td>
                <td className="p-1"><Input type="number" value={s.min_g} onChange={(e) => update(i, "min_g", e.target.value)}
                                            data-testid={`slab-min-${i}`} className="h-8 rounded-md mono" /></td>
                <td className="p-1"><Input type="number" value={s.max_g} onChange={(e) => update(i, "max_g", e.target.value)}
                                            data-testid={`slab-max-${i}`} className="h-8 rounded-md mono" /></td>
                <td className="p-1 text-right">
                  <Button variant="outline" size="sm" onClick={() => remove(i)}
                          data-testid={`slab-remove-${i}`} className="h-8 rounded-md"><Trash2 size={13} /></Button>
                </td>
              </tr>
            ))}
            {slabs.length === 0 && (
              <tr><td colSpan={4} className="p-6 text-center text-muted-foreground text-[12px]">
                No slabs yet. Click "Add slab" to create the first one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between">
        <Button variant="outline" onClick={addSlab} data-testid="slab-add" className="rounded-md">
          <Plus size={13} className="mr-1" /> Add slab
        </Button>
        <Button onClick={save} disabled={busy} data-testid="slab-save"
                className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
          <Save size={13} className="mr-1" /> Save slabs
        </Button>
      </div>
    </section>
  );
}

/* --------------- ZONE MATRIX EDITOR --------------- */
function ZoneMatrixEditor({ courier, scopeTid, scopeBranch }) {
  const [items, setItems] = useState([]);
  const [zones, setZones] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [addForm, setAddForm] = useState({ pincode: "", city: "", state: "", zone: "" });
  const [busy, setBusy] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);
  const [fallbackSource, setFallbackSource] = useState(null);
  const fileRef = useRef(null);
  const ps = 25;
  const scopeParam = scopeTid || "platform";

  const params = useMemo(() => {
    const p = { courier, page, page_size: ps, q: q || undefined, tenant_id: scopeParam };
    if (scopeBranch) p.branch_id = scopeBranch;
    return p;
  }, [courier, page, q, scopeParam, scopeBranch]);

  const writeParams = useMemo(() => {
    const p = { courier, tenant_id: scopeParam };
    if (scopeBranch) p.branch_id = scopeBranch;
    return p;
  }, [courier, scopeParam, scopeBranch]);

  const load = useCallback(async () => {
    const { data } = await api.get("/rate-matrix/zones", { params });
    setItems(data.items || []);
    setZones(data.zones || []);
    setTotal(data.total || 0);
    setUsedFallback(!!data.used_fallback);
    setFallbackSource(data.fallback_source || null);
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/rate-matrix/zones/upload", fd,
        { params: writeParams, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Inserted ${data.inserted} · Updated ${data.updated} · Skipped ${data.skipped}`);
      setPage(1);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const download = () => {
    const p = new URLSearchParams({ courier, tenant_id: scopeParam });
    if (scopeBranch) p.set("branch_id", scopeBranch);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/rate-matrix/zones/export?${p.toString()}`;
    const token = localStorage.getItem("token") || "";
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${courier}_zones.xlsx`;
        a.click();
      })
      .catch(() => toast.error("Download failed"));
  };

  const addOne = async () => {
    if (!addForm.pincode || !addForm.zone) return toast.error("Pincode + Zone required");
    try {
      await api.post("/rate-matrix/zones/mapping", addForm, { params: writeParams });
      toast.success("Mapping added");
      setAddForm({ pincode: "", city: "", state: "", zone: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  const removeOne = async (id) => {
    if (!confirm("Delete this mapping?")) return;
    try {
      await api.delete(`/rate-matrix/zones/mapping/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const clearAll = async () => {
    const scopeText = scopeBranch ? "this branch" : (scopeParam === "platform" ? "Platform default" : "this company");
    if (!confirm(`Delete ALL zone mappings for ${courier} at ${scopeText}? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete("/rate-matrix/zones", { params: writeParams });
      toast.success(`Deleted ${data.deleted}`);
      load();
    } catch (e) { toast.error("Clear failed"); }
  };

  return (
    <section className="space-y-3" data-testid="zone-matrix-editor">
      {usedFallback && (
        <div className="text-[12px] p-2 border border-amber-300 bg-amber-50 rounded-md text-amber-900"
             data-testid="zone-fallback-banner">
          <b>Fallback active.</b>{" "}
          {scopeBranch
            ? <>This branch has no custom zone mappings for {courier} yet — the {fallbackSource === "platform" ? "Mailit Default" : "Company Default"} matrix is used at booking time. Add pincodes here to create a branch-specific override (e.g. Mumbai Branch → 400001 as <b>Local</b>).</>
            : <>This company has no custom zone mappings for {courier} — Mailit Default is used at booking time. Add pincodes here to create a company override.</>}
        </div>
      )}
      <div className="flex flex-wrap gap-2" data-testid="zone-stats">
        {zones.map(z => (
          <div key={z.zone} className="border border-border rounded-md px-2 py-1 bg-card text-[11px]">
            <b>{z.zone}</b> <span className="mono text-muted-foreground">· {z.count}</span>
          </div>
        ))}
        {zones.length === 0 && <div className="text-[11px] text-muted-foreground">No zones at this scope yet</div>}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
                data-testid="zone-search" placeholder="Search PIN / city / zone…"
                className="h-9 rounded-md w-64" />
        <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={upload} className="hidden" data-testid="zone-file-input" />
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}
                data-testid="zone-import" className="h-9 rounded-md">
          <Upload size={13} className="mr-1" /> Import Excel
        </Button>
        <Button variant="outline" size="sm" onClick={download} data-testid="zone-export" className="h-9 rounded-md">
          <Download size={13} className="mr-1" /> Export Excel
        </Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={clearAll} data-testid="zone-clear"
                className="h-9 rounded-md text-red-700 border-red-300 hover:bg-red-50">
          <Trash2 size={13} className="mr-1" /> Clear all
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 border border-border rounded-md p-2 bg-muted/30">
        <Input placeholder="PIN Code" value={addForm.pincode}
                onChange={(e) => setAddForm({ ...addForm, pincode: e.target.value })}
                data-testid="zone-add-pin" className="h-9 rounded-md" />
        <Input placeholder="City" value={addForm.city}
                onChange={(e) => setAddForm({ ...addForm, city: e.target.value })}
                data-testid="zone-add-city" className="h-9 rounded-md" />
        <Input placeholder="State" value={addForm.state}
                onChange={(e) => setAddForm({ ...addForm, state: e.target.value })}
                data-testid="zone-add-state" className="h-9 rounded-md" />
        <Input placeholder="Zone (e.g. Metro, Zone A, ROI)" value={addForm.zone}
                onChange={(e) => setAddForm({ ...addForm, zone: e.target.value })}
                data-testid="zone-add-zone" className="h-9 rounded-md" />
        <Button onClick={addOne} data-testid="zone-add-btn"
                className="h-9 rounded-md bg-primary text-primary-foreground">
          <Plus size={13} className="mr-1" /> Add
        </Button>
      </div>
      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm mono">
          <thead className="bg-muted text-[10px] uppercase tracking-widest">
            <tr><th className="p-2 text-left">PIN</th>
                <th className="p-2 text-left">City</th>
                <th className="p-2 text-left">State</th>
                <th className="p-2 text-left">Zone</th>
                <th className="p-2 w-14"></th></tr>
          </thead>
          <tbody>
            {items.map(m => (
              <tr key={m.id} className="border-t border-border" data-testid={`zone-row-${m.pincode}`}>
                <td className="p-2">{m.pincode}</td>
                <td className="p-2">{m.city}</td>
                <td className="p-2">{m.state}</td>
                <td className="p-2"><span className="border border-border rounded-md px-1.5 py-0.5 text-[10px]">{m.zone}</span></td>
                <td className="p-2 text-right">
                  <Button variant="outline" size="sm" onClick={() => removeOne(m.id)}
                          data-testid={`zone-remove-${m.pincode}`} className="h-7 rounded-md">
                    <Trash2 size={11} />
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground text-[12px]">
                <FileSpreadsheet size={20} className="mx-auto mb-2 opacity-40" />
                No mappings in this scope. Import an Excel file to seed it.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {total > ps && (
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span data-testid="zone-total">Total {total}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    data-testid="zone-prev" className="h-7 rounded-md">Prev</Button>
            <span className="px-2 py-1 mono">Page {page} / {Math.ceil(total / ps)}</span>
            <Button variant="outline" size="sm" disabled={page * ps >= total}
                    onClick={() => setPage(p => p + 1)}
                    data-testid="zone-next" className="h-7 rounded-md">Next</Button>
          </div>
        </div>
      )}
    </section>
  );
}

/* --------------- COPY RATE MATRIX DIALOG --------------- */
function CopyDialog({ open, onClose, tenants, isPlatformAdmin, userTenantId, couriers, serviceTypes }) {
  const [fromTid, setFromTid] = useState("platform");
  const [toTid, setToTid] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [includeZones, setIncludeZones] = useState(true);
  const [includeSlabs, setIncludeSlabs] = useState(true);
  const [includeRates, setIncludeRates] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setResult(null);
      if (!isPlatformAdmin) {
        setFromTid("platform");        // company_admin can only copy from platform default
        setToTid(userTenantId || "");
      }
    }
  }, [open, isPlatformAdmin, userTenantId]);

  const submit = async () => {
    if (!toTid) return toast.error("Choose a destination company");
    if (fromTid === toTid) return toast.error("Source and destination must differ");
    setBusy(true);
    try {
      const { data } = await api.post("/rate-matrix/copy", {
        from_tenant_id: fromTid, to_tenant_id: toTid, overwrite,
        include_zones: includeZones, include_slabs: includeSlabs, include_rates: includeRates,
      });
      setResult(data);
      toast.success(`Copied · ${data.stats.zones} zones · ${data.stats.slabs} slabs · ${data.stats.rates} rates`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Copy failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="rm-copy-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Copy Rate Matrix</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-[12px] text-muted-foreground">
            Clone Zone Matrix + Weight Slabs + Rate cells from one company to another. Existing rates in the target are kept unless "Overwrite" is enabled.
          </p>

          <label className="block">
            <span className="overline">Copy From</span>
            <select value={fromTid} onChange={(e) => setFromTid(e.target.value)}
                    disabled={!isPlatformAdmin}
                    data-testid="rm-copy-from"
                    className="mt-1 h-10 w-full rounded-md border border-border bg-card px-3 text-sm">
              <option value="platform">🌐 Mailit Default (Platform)</option>
              {isPlatformAdmin && tenants.map(t => (
                <option key={t.id} value={t.id}>🏢 {t.name} · {t.code}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="overline">Copy To</span>
            <select value={toTid} onChange={(e) => setToTid(e.target.value)}
                    disabled={!isPlatformAdmin}
                    data-testid="rm-copy-to"
                    className="mt-1 h-10 w-full rounded-md border border-border bg-card px-3 text-sm">
              <option value="">— Choose company —</option>
              {isPlatformAdmin && <option value="platform">🌐 Mailit Default (Platform)</option>}
              {(isPlatformAdmin ? tenants : tenants.filter(t => t.id === userTenantId)).map(t => (
                <option key={t.id} value={t.id}>🏢 {t.name} · {t.code}</option>
              ))}
              {!isPlatformAdmin && !tenants.length && userTenantId && (
                <option value={userTenantId}>🏢 Your Company</option>
              )}
            </select>
          </label>

          <div className="grid grid-cols-3 gap-2 text-[12px]">
            <label className="flex items-center gap-2"><input type="checkbox" checked={includeZones} onChange={(e) => setIncludeZones(e.target.checked)} data-testid="rm-copy-include-zones" className="accent-[#BE1E1E]" /> Zones</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={includeSlabs} onChange={(e) => setIncludeSlabs(e.target.checked)} data-testid="rm-copy-include-slabs" className="accent-[#BE1E1E]" /> Weight Slabs</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={includeRates} onChange={(e) => setIncludeRates(e.target.checked)} data-testid="rm-copy-include-rates" className="accent-[#BE1E1E]" /> Rate Cells</label>
          </div>

          <label className="flex items-center gap-2 text-[12px]">
            <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)}
                    data-testid="rm-copy-overwrite" className="accent-[#BE1E1E]" />
            Overwrite existing values in destination
          </label>

          {result && (
            <div className="border border-emerald-300 bg-emerald-50 rounded-md p-2 text-[12px]"
                 data-testid="rm-copy-result">
              Copied · <b>{result.stats.zones}</b> zones · <b>{result.stats.slabs}</b> slab configs · <b>{result.stats.rates}</b> rate cells.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="rm-copy-cancel">Close</Button>
          <Button onClick={submit} disabled={busy || !toTid}
                  data-testid="rm-copy-submit"
                  className="bg-primary text-primary-foreground shadow-brut-orange">
            <Copy size={13} className="mr-1" /> {busy ? "Copying…" : "Copy Rate Matrix"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------- TRANSIT TIME (TAT) EDITOR --------------- */
function TransitTimeEditor({ courier, service, scopeTid, scopeBranch: parentBranch }) {
  const [zones, setZones] = useState([]);
  const [entries, setEntries] = useState([]);
  const [scopeBranch, setScopeBranch] = useState(parentBranch || "");
  useEffect(() => { setScopeBranch(parentBranch || ""); }, [parentBranch]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const params = useMemo(() => {
    const p = { courier, service_type: service, tenant_id: scopeTid || "platform" };
    if (scopeBranch) p.branch_id = scopeBranch;
    return p;
  }, [courier, service, scopeTid, scopeBranch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rate-matrix/transit", { params });
      setZones(data.zones || []);
      setEntries(data.entries || []);
    } catch (e) { toast.error("Could not load TAT"); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  // Load branches for the company scope so admins can pin TAT per branch.
  useEffect(() => {
    if (!scopeTid || scopeTid === "platform") { setBranches([]); return; }
    api.get("/branches", { params: { include_all: 1 } })
       .then(r => setBranches((r.data || []).filter(b => b.tenant_id === scopeTid || !b.tenant_id)))
       .catch(() => setBranches([]));
  }, [scopeTid]);

  const patch = (zone, key, value) => {
    setEntries(rows => rows.map(r => r.zone === zone
      ? { ...r, [key]: value === "" ? null : Number(value), used_fallback: false }
      : r));
  };

  const save = async () => {
    // Validate
    for (const r of entries) {
      if (r.tat_min_days == null && r.tat_max_days == null) continue;
      const mn = r.tat_min_days ?? r.tat_max_days;
      const mx = r.tat_max_days ?? r.tat_min_days;
      if (mn < 0 || mx < 0) { toast.error(`Negative TAT for ${r.zone}`); return; }
      if (mn > mx) { toast.error(`Min > Max for ${r.zone}`); return; }
    }
    setBusy(true);
    try {
      await api.put("/rate-matrix/transit",
        { entries: entries.map(r => ({ zone: r.zone,
                                        tat_min_days: r.tat_min_days,
                                        tat_max_days: r.tat_max_days,
                                        active: r.active !== false })) },
        { params });
      toast.success("Transit times saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 text-center text-muted-foreground text-sm">Loading…</div>;

  const isCompanyScope = scopeTid && scopeTid !== "platform";
  return (
    <section className="space-y-3" data-testid="transit-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] text-muted-foreground max-w-2xl">
          Configure Turnaround Time (TAT) per <b>Zone</b> for <b>{courier} · {service}</b>.
          Save a range like <span className="mono">2 – 3 days</span>. The engine picks the most specific
          entry using this fallback order: <b>Branch → Company → Platform default</b>.
          Empty rows fall back to the platform default automatically.
        </div>
        {isCompanyScope && (
          <div className="flex items-center gap-2">
            <span className="overline">Branch scope</span>
            <select value={scopeBranch}
                    onChange={(e) => setScopeBranch(e.target.value)}
                    data-testid="transit-branch-selector"
                    className="h-9 rounded-md border border-input bg-background text-xs px-2 min-w-[220px]">
              <option value="">All Branches (company default)</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name || b.code || b.id}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="border border-border rounded-md overflow-hidden bg-card">
        <table className="w-full text-sm" data-testid="transit-table">
          <thead className="bg-muted text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-2 text-left w-48">Zone</th>
              <th className="p-2 text-left">TAT Min (days)</th>
              <th className="p-2 text-left">TAT Max (days)</th>
              <th className="p-2 text-left">Display</th>
              <th className="p-2 text-left">Source</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((r) => {
              const disp = r.tat_min_days != null && r.tat_max_days != null
                ? (r.tat_min_days === r.tat_max_days
                    ? `${r.tat_min_days} day${r.tat_min_days === 1 ? "" : "s"}`
                    : `${r.tat_min_days}–${r.tat_max_days} days`)
                : "—";
              return (
                <tr key={r.zone} className="border-t border-border"
                    data-testid={`transit-row-${r.zone}`}>
                  <td className="p-2 font-bold">{r.zone}</td>
                  <td className="p-2">
                    <Input type="number" min="0" step="1"
                            value={r.tat_min_days ?? ""}
                            onChange={(e) => patch(r.zone, "tat_min_days", e.target.value)}
                            data-testid={`transit-min-${r.zone}`}
                            className="h-9 w-24 mono" placeholder="—" />
                  </td>
                  <td className="p-2">
                    <Input type="number" min="0" step="1"
                            value={r.tat_max_days ?? ""}
                            onChange={(e) => patch(r.zone, "tat_max_days", e.target.value)}
                            data-testid={`transit-max-${r.zone}`}
                            className="h-9 w-24 mono" placeholder="—" />
                  </td>
                  <td className="p-2 mono text-[12px]">{disp}</td>
                  <td className="p-2 text-[10px] text-muted-foreground">
                    {r.used_fallback ? (
                      <span className="text-amber-700">↳ platform default</span>
                    ) : (r.tat_min_days == null && r.tat_max_days == null) ? (
                      <span>not configured</span>
                    ) : (
                      <span className="text-emerald-700">this scope</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] text-muted-foreground">
          Range appears as <span className="mono">2–3d</span> in the Rate Comparison ETA column. Clearing both fields
          removes the override so this zone reverts to the fallback chain.
        </div>
        <Button onClick={save} disabled={busy} data-testid="transit-save"
                className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
          <Save size={13} className="mr-1" /> {busy ? "Saving…" : "Save transit times"}
        </Button>
      </div>
    </section>
  );
}
function SurchargesEditor({ courier, service, scopeTid, scopeBranch }) {
  const [row, setRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const params = useMemo(() => {
    const p = { courier, service_type: service, tenant_id: scopeTid || "platform" };
    if (scopeBranch) p.branch_id = scopeBranch;
    return p;
  }, [courier, service, scopeTid, scopeBranch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rate-matrix/surcharges", { params });
      setRow(data);
    } catch (e) { toast.error("Could not load surcharges"); }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const update = (k, v) => setRow(r => ({ ...r, [k]: v }));

  const save = async () => {
    if (!row) return;
    const body = {
      fuel_pct: Number(row.fuel_pct) || 0,
      handling_flat: Number(row.handling_flat) || 0,
      remote_area_flat: Number(row.remote_area_flat) || 0,
      insurance_pct: Number(row.insurance_pct) || 0,
      cod_flat: Number(row.cod_flat) || 0,
      other_flat: Number(row.other_flat) || 0,
      gst_pct: Number(row.gst_pct) || 18,
      transit_days: row.transit_days ? Number(row.transit_days) : null,
      active: row.active !== false,
    };
    setBusy(true);
    try {
      await api.put("/rate-matrix/surcharges", body, { params });
      toast.success("Surcharges saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 text-center text-muted-foreground text-sm">Loading…</div>;
  if (!row) return null;

  const fields = [
    { key: "fuel_pct", label: "Fuel Surcharge", suffix: "%", step: "0.01",
      help: "Percentage of base freight added as fuel surcharge before GST." },
    { key: "handling_flat", label: "Handling Charge", prefix: "₹", step: "0.01",
      help: "Flat rupee amount added to every shipment for handling." },
    { key: "gst_pct", label: "GST", suffix: "%", step: "0.01",
      help: "GST rate applied on (freight + fuel + handling + insurance)." },
  ];

  return (
    <section className="space-y-3" data-testid="surcharges-editor">
      {row.used_fallback && (
        <div className="text-[12px] p-2 border border-amber-300 bg-amber-50 rounded-md text-amber-900"
             data-testid="surcharges-fallback-banner">
          <b>Fallback active.</b> Showing Mailit Default surcharges for {courier} · {service}. Saving here creates a company override.
        </div>
      )}
      <div className="border border-border rounded-md overflow-hidden bg-card">
        <table className="w-full text-sm" data-testid="surcharges-table">
          <thead className="bg-muted text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-2 text-left w-64">Charge</th>
              <th className="p-2 text-left">Value</th>
              <th className="p-2 text-left">Applied as</th>
            </tr>
          </thead>
          <tbody>
            {fields.map(f => (
              <tr key={f.key} className="border-t border-border" data-testid={`surcharge-row-${f.key}`}>
                <td className="p-2 font-bold">
                  {f.label}
                  <div className="text-[10px] font-normal text-muted-foreground">{f.key}</div>
                </td>
                <td className="p-2">
                  <div className="flex items-center gap-1 w-40">
                    {f.prefix && <span className="mono text-muted-foreground">{f.prefix}</span>}
                    <Input type="number" step={f.step} min="0"
                            value={row[f.key] ?? 0}
                            onChange={(e) => update(f.key, e.target.value)}
                            data-testid={`surcharge-input-${f.key}`}
                            className="h-9 rounded-md mono" />
                    {f.suffix && <span className="mono text-muted-foreground">{f.suffix}</span>}
                  </div>
                </td>
                <td className="p-2 text-[11px] text-muted-foreground">{f.help}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] text-muted-foreground max-w-2xl">
          Applies to <b>{courier} · {service}</b> for scope <b>{scopeTid === "platform" || !scopeTid ? "Platform default" : "this company"}</b>.
          Advanced surcharges (remote area, insurance %, COD, other) remain configurable via PUT API.
        </div>
        <Button onClick={save} disabled={busy} data-testid="surcharge-save"
                className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
          <Save size={13} className="mr-1" /> {busy ? "Saving…" : "Save surcharges"}
        </Button>
      </div>
    </section>
  );
}
